import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import {
  AidaSurveyEligibility,
  AidaPayload,
  AidaQbpSelectionQuota,
  SurveyFeaturesState,
} from '../../shared/interfaces/aida.interface';
import { AIDA_ELIGIBILITY_ERROR_MESSAGES } from './ai-delivery-assistant.const';
import { AidaPayloadService } from '../../shared/services/aida/aida-payload/aida-payload.service';
import { QUALIFICATIONS_CATEGORIES } from '../../shared/constants/qualification-categories';
import { SurveyPayload } from '../../shared/interfaces/survey-meta-data.interface';
import { FormGroup } from '@angular/forms';
import { SurveyStatus } from '../../shared/utils/survey-status';
import { BuyerSurveyResponse } from '../../shared/interfaces/survey.interface';
import { isEqual } from '../../create-surveys/utils/deep-compare';
import { AidaQuotaFilter } from './domain/aida-quota-filter/aida-quota-filter';

@Component({
  selector: 'ps-ui-ai-delivery-assistant',
  templateUrl: './ai-delivery-assistant.component.html',
  styleUrls: ['./ai-delivery-assistant.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiDeliveryAssistantComponent
  implements OnInit, OnChanges, OnDestroy
{
  constructor(private _aidaPayloadService: AidaPayloadService) {}

  @Input()
  public surveyPayload: SurveyPayload; // survey payload that going to be send in request

  @Input()
  public surveyDetails: BuyerSurveyResponse; // survey received from response

  @Input()
  public set initialEligibility(
    eligibility: AidaSurveyEligibility | undefined
  ) {
    const error_messages = eligibility?.error_messages || [];
    this._initialErrors$.next(
      this._filterLaunchEligibilityMessages(error_messages)
    );
  }

  @Input()
  public set surveyFeatures(features: SurveyFeaturesState | undefined) {
    const errorMessages = this._checkAErrorMessages({
      isFeotEnabled: features?.isFeotEnabled || false,
      isMarginMaxEnabled: features?.isMarginMaxEnabled || false,
      isQbpEnabled: features?.isQbpEnabled || false,
      isSoftLaunchEnabled: features?.isSoftLaunchEnabled || false,
    });
    this._localErrors$.next(errorMessages);
  }

  @Input()
  public aidaQuotasForSelection: AidaQbpSelectionQuota[];

  @Input()
  form: FormGroup;

  public aidaSelectedQuota: AidaQbpSelectionQuota;
  public isEnableAidaSurvey: boolean = true;
  public eligibleAidaQuotasForSelection: AidaQbpSelectionQuota[] = [];

  private readonly _initialErrors$ = new BehaviorSubject<string[]>([]);
  private readonly _localErrors$ = new BehaviorSubject<string[]>([]);
  private readonly _subscriptions = new Subscription();

  ngOnInit(): void {
    //
    this._handleQuotaSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.surveyPayload?.currentValue ||
      changes.aidaQuotasForSelection?.currentValue ||
      changes.surveyFeatures?.currentValue
    ) {
      this._findSelectedQuota();
      this._disableQuotaSelection();
    }

    if (changes.surveyDetails?.currentValue) {
      this._initializeSurveyDetails();
      this._disableQuotaSelection();
    }
  }

  public get aidaEligibility$(): Observable<AidaSurveyEligibility> {
    return combineLatest([this._initialErrors$, this._localErrors$]).pipe(
      map(([initialErrorMessages, localErrorMessages]) => {
        if (this.surveyDetails.mc.SVP) {
          return {
            eligible: false,
            error_messages: [AIDA_ELIGIBILITY_ERROR_MESSAGES.MCSVP],
          };
        }
        if (
          !this.surveyDetails?.aida?.enabled &&
          this.surveyDetails.ps_survey_status !== SurveyStatus.Draft
        ) {
          return {
            eligible: false,
            error_messages: [],
          };
        }

        const allErrors = [...initialErrorMessages, ...localErrorMessages];
        return {
          eligible: allErrors.length === 0,
          error_messages: allErrors,
        };
      })
    );
  }

  private _filterLaunchEligibilityMessages(messages: string[]) {
    return messages.filter((errorMessage) => {
      const message = errorMessage.toLowerCase();
      return (
        !message.includes('feot') &&
        !message.includes('qbp') &&
        !message.includes('soft launch') &&
        !message.includes('margin max')
      );
    });
  }

  private _checkAErrorMessages({
    isFeotEnabled,
    isQbpEnabled,
    isMarginMaxEnabled,
    isSoftLaunchEnabled,
  }: SurveyFeaturesState): string[] {
    const messages = [
      { enabled: isFeotEnabled, message: AIDA_ELIGIBILITY_ERROR_MESSAGES.FEOT },
      { enabled: isQbpEnabled, message: AIDA_ELIGIBILITY_ERROR_MESSAGES.QBP },
      {
        enabled: isMarginMaxEnabled,
        message: AIDA_ELIGIBILITY_ERROR_MESSAGES.MARGIN_MAX,
      },
      {
        enabled: isSoftLaunchEnabled,
        message: AIDA_ELIGIBILITY_ERROR_MESSAGES.SOFT_LAUNCH,
      },
    ];

    return messages
      .filter(({ enabled }) => enabled)
      .map(({ message }) => message);
  }

  private _disableQuotaSelection() {
    const aidaSelectedQuotaControl = this.form.get('aidaSelectedQuota');
    if (!this._isSurveyDraft()) {
      return aidaSelectedQuotaControl?.disable(); // disable input in case of live survey
    }
  }

  /**
   * Check survey is in draft status or not
   */
  private _isSurveyDraft(): boolean {
    return (
      !!this.surveyDetails?.ps_survey_status &&
      this.surveyDetails.ps_survey_status === SurveyStatus.Draft
    );
  }

  private _handleQuotaSelection() {
    const _subscription = this.form
      .get('aidaSelectedQuota')
      ?.valueChanges.pipe(
        debounceTime(100),
        distinctUntilChanged((prev, curr) =>
          isEqual(prev?.selectedQuotaIds, curr?.selectedQuotaIds)
        )
      )
      .subscribe((quota) => {
        this.aidaSelectedQuota = quota;
        this.setAidaPayload(this.surveyPayload.aida.enabled);
      });
    this._subscriptions.add(_subscription);
  }

  setAidaPayload(isAidaEnabled: boolean) {
    const payload: AidaPayload = {
      enabled: isAidaEnabled,
      selected_quota_ids: isAidaEnabled
        ? this.aidaSelectedQuota?.selectedQuotaIds
        : [],
    };
    this._updateSelectedQuota();
    this._aidaPayloadService.setAidaPayload(payload);
  }

  private _findSelectedQuota(): void {
    // Filter for Age, Gender and Region Quotas
    const [, countryCode] = (
      this.surveyPayload.survey_localization || ''
    ).split('_');
    const {
      eligible = [],
      ineligible = [],
      errorMessages = [],
    } = new AidaQuotaFilter(
      this.surveyPayload.click_balancing
        ? this.surveyPayload.estimated_clicks!
        : this.surveyPayload.completes_required,
      this._aidaPayloadService.getMappedDBQualificationCodes(),
      countryCode,
      this.surveyPayload.quotas ?? [],
      this.aidaQuotasForSelection,
      false
    ).filter();

    this.eligibleAidaQuotasForSelection = eligible as AidaQbpSelectionQuota[];

    // If Quotas are other than Age, Gender and Loaction or Empty, will show message to user and AIDA will be disabled
    if (errorMessages.length) {
      this._localErrors$.next(errorMessages);
    }

    const aida = this.surveyPayload.aida;
    this.form.get('aidaSelectedQuota')?.enable(); // enable the input button for selection to prevent disablitity from mc surveys for draft survey
    this.eligibleAidaQuotasForSelection = this._prepareQuotasForSelection(
      this.eligibleAidaQuotasForSelection,
      aida
    );
    const [defaultSelectedQuota] = this.eligibleAidaQuotasForSelection; // if nesting applied, then 1st quota will be nested.
    const isNestedQuotaAppliedOnAida =
      defaultSelectedQuota?.category === QUALIFICATIONS_CATEGORIES.NESTED;
    // Handle case where category is NESTED
    if (
      (isNestedQuotaAppliedOnAida || !aida.enabled) &&
      this._isSurveyDraft()
    ) {
      this.aidaSelectedQuota = defaultSelectedQuota;
      this._updateSelectedQuota();
      if (isNestedQuotaAppliedOnAida) {
        this.form.get('aidaSelectedQuota')?.disable(); // disable input in case of input
      }
      return;
    }

    // Ensure AIDA is enabled, has selected quotas, and a valid category
    if (aida.enabled && aida.selected_quota_ids?.length && aida.category) {
      const matchedQuota = this._findMatchingQuota(aida.category);
      this.aidaSelectedQuota =
        matchedQuota ?? this.eligibleAidaQuotasForSelection[0];
      this._updateSelectedQuota();
    }
  }

  private _prepareQuotasForSelection(
    quotaSelections: AidaQbpSelectionQuota[],
    aida: AidaPayload
  ): AidaQbpSelectionQuota[] {
    const nestedSelections = (quotaSelections ?? []).filter(
      (q) => q.category === QUALIFICATIONS_CATEGORIES.NESTED
    );

    // If there is a nested quota in the Draft survey, we will show only that nested quota as option and by default it will be selected. Otherwise, all options will show.
    if (this._isSurveyDraft()) {
      return nestedSelections.length ? nestedSelections : quotaSelections;
    }

    // If there is a nested quota in the Live Edit survey and AIDA selection is that nesting too, we will show only that nested quota as option and by default it will be selected. Otherwise, all options will show.
    return nestedSelections.length &&
      aida.category === QUALIFICATIONS_CATEGORIES.NESTED
      ? nestedSelections
      : quotaSelections;
  }

  /**
   * Finds a matching quota based on category.
   * Prioritizes "household income" as a special case.
   */
  private _findMatchingQuota(
    category: string
  ): AidaQbpSelectionQuota | undefined {
    const formattedCategory = category.toLowerCase().trim().replace(/_/g, '');

    return this.eligibleAidaQuotasForSelection.find(
      (quota: AidaQbpSelectionQuota) => {
        const formattedQuotaCategory = quota.category
          .toLowerCase()
          .trim()
          .replace(/_/g, '');

        if (formattedQuotaCategory === 'householdincome') {
          return formattedQuotaCategory.includes(formattedCategory);
        }
        return formattedQuotaCategory === formattedCategory;
      }
    );
  }

  /**
   * Updates the selected quota in the form.
   */
  private _updateSelectedQuota(): void {
    this.form.get('aidaSelectedQuota')?.patchValue(this.aidaSelectedQuota);
  }

  private _initializeSurveyDetails() {
    this.isEnableAidaSurvey = !!this.surveyDetails.aida?.enabled;
  }

  public isLaunchedSurvey() {
    return (
      !!this.surveyDetails?.ps_survey_status &&
      this.surveyDetails?.ps_survey_status !== SurveyStatus.Draft
    );
  }

  public shouldShowQuotaSelection() {
    if (!this.isLaunchedSurvey()) {
      return (
        this.eligibleAidaQuotasForSelection.length &&
        this.surveyPayload.aida.enabled
      );
    }
    return (
      this.isLaunchedSurvey() &&
      this.surveyPayload?.aida?.enabled &&
      !!this.surveyPayload?.aida?.selected_quota_ids?.length
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
